import { computed, inject, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import { pick } from 'lodash'

import { WSInstance, WSResponse } from '@/services/ws/utils/types'
import type { AIChatMessageData } from '../utils/types'
import { AccountSuggestion } from '@/entities/assets/utils/types'
import { AccountCandidate } from '../accountSuggestion/utils/types'
import { AIType } from '../utils/enums'

import { generateId } from '@/entities/utils/helpers'
import { handleCatchedError } from '@/helpers/common'
import { createSuggestionTimeout } from '../utils/timeout'
import { prepareMessages } from '../utils/helpers'
import { readSuggestion } from '../accountSuggestion/utils/helpers'

import { useUserStore } from '@/store/user'

/**
 * The assistant behind the mortgage step's suggested match.
 *
 * Answers the same question as useAIAccountSuggestionStore — which of the
 * offered accounts fits the user's sentence, or none — over the repository's
 * own existing liability accounts (see MortgageStep.vue), the same source
 * `accountsOf` already resolves for the ownership step's own accounts. A
 * second store rather than a second call into that one: the mortgage step is
 * asked within the same wizard session as the ownership step, and the two
 * questions must not share `hasAsked`/`suggestion` state, or asking one would
 * leave the other's "Ask AI" row permanently hidden, or overwrite its answer.
 *
 * Sends the same AIType.ACCOUNT_SUGGESTION as that store — the prompt reads
 * a sentence against a list of candidate accounts and picks one or none,
 * which is exactly this question too, just for a different record.
 *
 * A suggestion is an aid, never a requirement, so every failure here is
 * silent to the user: no toast, no error state, no suggested row. Failures
 * are still reported through handleCatchedError, so Faro sees them.
 */
export const useAIMortgageAccountSuggestionStore = defineStore('ai-mortgage-account-suggestion', () => {
  // INIT
  const userStore = useUserStore()

  const ws = inject<WSInstance>('ws')

  const requestId = ref(`tmp_${generateId()}`)

  const suggestion = ref<AccountSuggestion>()
  const loading = ref(false)

  /**
   * Whether this question has been asked at all, regardless of the answer.
   * The user asks by clicking "Ask AI" — this is what tells that control to
   * stop offering itself once it has done its job.
   */
  const asked = ref(false)

  /*
   * The candidates the live request was sent with. An answer names an id, and
   * the only ids this store will accept are the ones it offered.
   */
  let candidateIds: string[] = []

  /** What has already been asked, so walking back into the step does not re-ask. */
  let askedFor = ''

  const timeout = createSuggestionTimeout()

  // GETTERS

  const getSuggestion = computed(() => suggestion.value)

  const isLoading = computed(() => loading.value)

  const hasAsked = computed(() => asked.value)

  // ACTIONS

  const reset = () => {
    requestId.value = `tmp_${generateId()}`
    suggestion.value = undefined
    timeout.stop()
    loading.value = false
    asked.value = false
    candidateIds = []
    askedFor = ''
  }

  /**
   * Asks which of `accounts` the described mortgage belongs to.
   *
   * Does nothing without a sentence — with no words to go on there is no
   * context to choose from — and nothing without candidates, since there is
   * then nothing to pick between.
   */
  const suggest = async (params: {
    sentence: string
    flow: string
    transactionType: string
    accounts: AccountCandidate[]
  }) => {
    const { sentence, flow, transactionType, accounts } = params

    if (!sentence.trim() || !accounts.length) return
    // The websocket is absent when VITE_APP_WS_DISABLED is set.
    if (!ws) return

    const key = `${flow}|${transactionType}|${sentence}|${accounts.map(item => item.id).join(',')}`
    if (key === askedFor) return

    askedFor = key
    asked.value = true
    requestId.value = `tmp_${generateId()}`
    candidateIds = accounts.map(account => account.id)
    suggestion.value = undefined
    loading.value = true

    timeout.start(() => {
      loading.value = false
      askedFor = ''
      handleCatchedError('Mortgage account suggestion timed out — no answer for the account')
    })

    const { messages } = prepareMessages(sentence)

    const messageData: AIChatMessageData = {
      ai_type: AIType.ACCOUNT_SUGGESTION,
      messages,
      dynamic_context: {
        flow,
        transaction_type: transactionType,
        accounts_list: JSON.stringify(accounts
          .map(item => pick(item, ['id', 'name', 'type']))
        ),
      },
    }

    ws.send('ai', 'chat', requestId.value, messageData)
  }

  watch(
    () => userStore.isAuthenticated,
    value => {
      if (!value) return
      ws?.subscribe(
        'ai',
        'chat',
        (message: WSResponse<string>, responseId: string) => {
          if (responseId !== requestId.value) return

          try {
            if (message.errors || !message.data) {
              handleCatchedError(
                `Mortgage account suggestion failed: ${message.errors?.join(', ') ?? 'empty answer'}`,
              )
              return
            }

            const { suggestion: read, error } = readSuggestion(message.data, candidateIds)

            if (error) {
              handleCatchedError(error)
              return
            }

            // Absent when the answer was "nothing fits", which is not an error.
            if (read) suggestion.value = read
          } catch (e) {
            handleCatchedError(e as string)
          } finally {
            timeout.stop()
            loading.value = false
          }
        },
      )
    },
    { immediate: true },
  )

  return {
    isLoading,
    hasAsked,

    getSuggestion,

    suggest,
    reset,
  }
})
